/**
 * The Budget & Draws tab's sections — ONE list, read by three surfaces.
 *
 * Budget & Draws is a single page of stacked cards rather than several routes,
 * so its "sub-views" are anchors: the primary nav's hover flyout, the tab's own
 * sub-view strip and the deep-link param all read this list, which is what keeps
 * them in step. Exactly the arrangement the Contacts tab uses
 * (`contactsSections.js`) — see it for the original.
 *
 * Deep link: `…/budget-draws?section=<key>`. The tab consumes the param once,
 * scrolls to the anchor and strips it, so the URL never pins a stale section.
 */
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import PhotoCameraOutlined from "@mui/icons-material/PhotoCameraOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";

/** Query param the Budget & Draws tab reads to land on one of its sections. */
export const BUDGET_SECTION_PARAM = "section";

export const BUDGET_SECTIONS = {
  FEASIBILITY: "feasibility",
  CAPITAL: "capital",
  SCOPE_OF_WORK: "scope-of-work",
  DRAWS: "draws",
  INSPECTIONS: "inspections",
};

/** The DOM id the section card carries, and what the strip scrolls to. */
export const budgetSectionAnchor = (key) => `budget-section-${key}`;

/**
 * The strip is pinned, so a section scrolled to by `scrollIntoView` has to
 * clear it plus a little air — otherwise the card's own header lands underneath
 * the strip that sent you there. Spread onto every anchored section.
 */
export const BUDGET_SECTION_ANCHOR_SX = { scrollMarginTop: "76px" };

/**
 * @param {{ hasInspections?: boolean }} ctx
 *   `hasInspections` is DATA, not a product gate — only the tab knows whether
 *   any draw inspection has been extracted. The nav flyout calls this without
 *   it and therefore lists the four permanent sections; the tab passes the real
 *   flag so its own strip picks up Inspections when they exist. That asymmetry
 *   is deliberate: the flyout must not offer a jump to a card that isn't there.
 * @returns {Array<{ key: string, label: string, icon: Function }>}
 */
export const getBudgetDrawsSections = (ctx = {}) =>
  [
    {
      key: BUDGET_SECTIONS.FEASIBILITY,
      label: "Feasibility",
      icon: FactCheckOutlined,
    },
    {
      key: BUDGET_SECTIONS.CAPITAL,
      label: "Capital",
      icon: AccountBalanceOutlined,
    },
    {
      key: BUDGET_SECTIONS.SCOPE_OF_WORK,
      label: "Budget & Scope of Work",
      icon: TableChartOutlined,
    },
    { key: BUDGET_SECTIONS.DRAWS, label: "Draws", icon: PaymentsOutlined },
    ctx.hasInspections
      ? {
          key: BUDGET_SECTIONS.INSPECTIONS,
          label: "Inspections",
          icon: PhotoCameraOutlined,
        }
      : null,
  ].filter(Boolean);

export default getBudgetDrawsSections;
